import { applyTheme, DEFAULT_MODE, DEFAULT_THEME, isThemeId, type Mode, type ThemeId } from '@graal/tokens';
import faviconGraal from '@graal/tokens/logos/favicon-graal.svg?url';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  isModePreference,
  resolveMode,
  subscribeSystemMode,
  systemMode,
  type ModePreference,
} from './mode-preference';

const STORAGE_KEY = 'graal.theme';
/** Un favicon par thème : un seul aujourd'hui (Graphite), l'entrée d'une marque blanche demain. */
const FAVICONS: Record<ThemeId, string> = { graal: faviconGraal };

/** Ce qui est mémorisé : le thème et la PRÉFÉRENCE de mode (`system` compris). */
type Stored = { theme: ThemeId; mode: ModePreference };
/** Ce qui est appliqué : `mode` est toujours résolu (`dark` | `light`). */
type ThemeState = { theme: ThemeId; mode: Mode; preference: ModePreference };
type ThemeContextValue = ThemeState & {
  setTheme: (theme: ThemeId) => void;
  setMode: (mode: ModePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): Stored {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Record<string, unknown>;
    return {
      // Un identifiant inconnu — `or`, `ultraviolet`, `signal`, les trois directions de J2 retirées
      // le 2026-09-28 — retombe sur le défaut, sans erreur : il est encore dans des navigateurs.
      theme: isThemeId(raw['theme']) ? raw['theme'] : DEFAULT_THEME,
      mode: isModePreference(raw['mode']) ? raw['mode'] : DEFAULT_MODE,
    };
  } catch {
    return { theme: DEFAULT_THEME, mode: DEFAULT_MODE };
  }
}

function setFavicon(theme: ThemeId) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.append(link);
  }
  link.type = 'image/svg+xml';
  link.href = FAVICONS[theme];
}

/**
 * Applique le thème mémorisé (ou DEFAULT_THEME / DEFAULT_MODE) AVANT le premier rendu.
 * Appelé une fois depuis main.tsx : `index.html` ne fige aucun thème, et le thème par défaut
 * reste un seul changement dans `packages/tokens/src/themes.ts`.
 */
export function applyStoredTheme(): ThemeState {
  const stored = readStored();
  const mode = resolveMode(stored.mode);
  applyTheme(stored.theme, mode);
  setFavicon(stored.theme);
  return { theme: stored.theme, mode, preference: stored.mode };
}

/**
 * Thème (Graphite) × sombre / clair / système, appliqué sur <html data-theme data-mode> et
 * persisté (localStorage). La direction visuelle n'est plus un choix de l'utilisateur.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<Stored>(readStored);
  const system = useSyncExternalStore(subscribeSystemMode, systemMode, () => DEFAULT_MODE);
  const mode: Mode = stored.mode === 'system' ? system : stored.mode;

  useEffect(() => {
    applyTheme(stored.theme, mode);
    setFavicon(stored.theme);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // stockage indisponible (navigation privée) : thème non persisté
    }
  }, [stored, mode]);

  // Mises à jour fonctionnelles : setTheme et setMode appelés dans le même commit
  // ne s'écrasent pas l'un l'autre (le design lab pilote les deux depuis l'URL).
  const value = useMemo<ThemeContextValue>(
    () => ({
      theme: stored.theme,
      mode,
      preference: stored.mode,
      setTheme: (next) =>
        setStored((previous) => (previous.theme === next ? previous : { ...previous, theme: next })),
      setMode: (next) =>
        setStored((previous) => (previous.mode === next ? previous : { ...previous, mode: next })),
    }),
    [stored, mode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme doit être utilisé sous <ThemeProvider>.');
  return ctx;
}
